"use client";

import Image from "next/image";
import Link from "next/link";
import {useTranslations} from "next-intl";
import {type Locale, localePath} from "@/lib/i18n";

const locale: Locale = 'fa';

export default function Footer() {
    const tFooter = useTranslations('footer');
    const tBrand = useTranslations('brand');
    const tNav = useTranslations('nav');
    const tRadjoo = useTranslations('radjoo');
    const tProducts = useTranslations('products');
    const tPage = useTranslations('page');
    const tGeneral = useTranslations('general');
    const tSolutions = useTranslations('solutions');

    const footerSections = [
        {
            title: tFooter("products"),
            links: [
                {label: tNav("submenu.trackers.vehicleTracker"), href: localePath(locale, 'vehicle-tracker')},
                {label: tNav("submenu.trackers.personTracker"), href: localePath(locale, 'person-tracker')},
                {label: tRadjoo("title"), href: localePath(locale, 'radjoo')},
                {label: tProducts("title"), href: localePath(locale, 'products')},
            ],
        },
        {
            title: tFooter("services"),
            links: [
                {label: tGeneral("services.title"), href: localePath(locale, 'services')},
                {label: tPage("pms.title"), href: localePath(locale, 'pms')},
                {label: tSolutions("saas.title"), href: localePath(locale, 'saas')},
                {label: tSolutions("system-integration.title"), href: localePath(locale, 'system-integration')},
            ],
        },
        {
            title: tFooter("resources"),
            links: [
                {label: tSolutions("cost-reduction.title"), href: localePath(locale, 'cost-reduction')},
                {label: tSolutions("iot-monitoring.title"), href: localePath(locale, 'iot-monitoring')},
                {label: tSolutions("machine-learning.title"), href: localePath(locale, 'machine-learning')},
                {label: tSolutions("safety-in-commuting.title"), href: localePath(locale, 'safety-in-commuting')},
                {label: tSolutions("transportation-control.title"), href: localePath(locale, 'transportation-control')},
                {label: tSolutions("maintenance-optimization.title"), href: localePath(locale, 'maintenance-optimization')},
            ],
        },
        {
            title: tFooter("company"),
            links: [
                {label: tNav("contact"), href: localePath(locale, 'contactus')},
                {label: tGeneral("customers.title"), href: localePath(locale, 'customers')},
                {label: tGeneral("use-cases.title"), href: localePath(locale, 'use-cases')},
                {label: tGeneral("knowledge-base.title"), href: localePath(locale, 'knowledge-base')},
            ],
        },
    ];

    return (
        <footer
            className="text-slate-200"
            style={{
                background: 'var(--sm-ink)',
                padding: 'clamp(34px,4vw,56px) clamp(16px,4vw,44px) 26px',
            }}
        >
            <div className="mx-auto max-w-7xl text-right">
                <div
                    className="grid"
                    style={{gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 'clamp(22px, 2.8vw, 42px)'}}
                >
                    {/* Brand */}
                    <div>
                        <div className="mb-3.5 flex items-center gap-[11px]">
                            <span className="grid h-8 w-8 flex-none place-items-center rounded-[10px] bg-white">
                                <Image src="/logo.png" alt="" width={21} height={21} />
                            </span>
                            <span className="text-[16px] text-slate-200">{tBrand('name')}</span>
                        </div>
                        <p className="text-[13.5px] leading-relaxed text-slate-400">{tFooter('description')}</p>
                    </div>

                    {footerSections.map((section) => (
                        <div key={section.title}>
                            <div className="mb-3.5 text-[11.5px] font-bold tracking-[.1em] text-slate-500">
                                {section.title}
                            </div>
                            <ul className="m-0 flex list-none flex-col gap-[9px] p-0 text-sm">
                                {section.links.map((link) => (
                                    <li key={link.label}>
                                        <Link
                                            href={link.href}
                                            className="text-slate-300 transition-colors hover:text-[var(--sm-teal-400)]"
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Bottom bar */}
                <div
                    className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2.5 text-[12.5px] text-slate-500"
                    style={{marginTop: 'clamp(26px,3vw,40px)', paddingTop: '18px', borderTop: '1px solid rgba(255,255,255,.1)'}}
                >
                    <span>{tFooter('copyright')}</span>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <a
                            href="https://cafebazaar.ir/app/com.farjad.radjoo"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition-colors hover:text-slate-300"
                        >
                            {tFooter('downloadApp')}
                        </a>
                        <a
                            href="https://www.linkedin.com/company/farjad-system-pardaz-"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition-colors hover:text-slate-300"
                        >
                            LinkedIn
                        </a>
                        <a
                            href="https://www.facebook.com/share/1DNbxrknze/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition-colors hover:text-slate-300"
                        >
                            Facebook
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
